import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '../cn'

// InboxContactPanel — the contact context column of the inbox (design/
// components/inbox.html, col 3). Gives the agent the contact's details without
// leaving the thread: header, keyed sections, editable tags, free notes.

export interface ContactPanelRow {
  label: string
  value: ReactNode
  /** Tint the value (e.g. a sensitive priority). */
  tone?: 'sens' | 'warn' | 'ok'
}

export interface ContactPanelSection {
  title: string
  rows: ContactPanelRow[]
}

export interface ContactTag {
  label: string
  tone?: 'sens' | 'default'
}

export interface InboxContactPanelProps {
  contact: { name: string; initials: string; phone?: string }
  /** When set, the contact's name links to their profile — the panel is
   *  where an Asesor asks "who IS this person"; the answer page exists. */
  nameHref?: string | undefined
  sections?: ContactPanelSection[]
  tags?: ContactTag[]
  onAddTag?: () => void
  notes?: string
  className?: string
}

const valueTone = {
  sens: 'text-sens',
  warn: 'text-warn',
  ok: 'text-ok',
}

export function InboxContactPanel({
  contact,
  nameHref,
  sections = [],
  tags,
  onAddTag,
  notes,
  className,
}: InboxContactPanelProps) {
  return (
    <aside
      aria-label={`Contacto: ${contact.name}`}
      className={cn('h-full overflow-y-auto overflow-x-hidden bg-surface', className)}
    >
      <div className="border-b border-line px-4 pb-3.5 pt-[18px] text-center">
        <span
          aria-hidden
          className="mx-auto flex size-[52px] items-center justify-center rounded-full bg-[image:var(--brand-gradient)] text-[16px] font-semibold text-white"
        >
          {contact.initials}
        </span>
        {nameHref ? (
          <Link
            to={nameHref}
            className="mt-[9px] text-[14px] font-bold text-text underline-offset-2 hover:text-link hover:underline"
          >
            {contact.name}
          </Link>
        ) : (
          <div className="mt-[9px] text-[14px] font-bold text-text">{contact.name}</div>
        )}
        {contact.phone && <div className="mt-0.5 text-[11.5px] text-text-2">{contact.phone}</div>}
      </div>

      {sections.map((section) => (
        <div key={section.title} className="border-b border-line px-4 py-[13px]">
          <div className="mb-[9px] text-[10px] font-bold uppercase tracking-[0.06em] text-text-2">
            {section.title}
          </div>
          {section.rows.map((row, i) => (
            <div key={i} className="flex justify-between gap-2.5 py-1 text-[11.5px]">
              <span className="text-text-2">{row.label}</span>
              <span
                className={cn(
                  'text-right font-semibold text-text',
                  row.tone && valueTone[row.tone],
                )}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      ))}

      {/* An EMPTY array is truthy, so this used to render a permanently empty
          "Etiquetas" section. Show it only when there is something in it, or a
          real way to add one. */}
      {tags && (tags.length > 0 || onAddTag) && (
        <div className="border-b border-line px-4 py-[13px]">
          <div className="mb-[9px] text-[10px] font-bold uppercase tracking-[0.06em] text-text-2">
            Etiquetas
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag, i) => (
              <span
                key={i}
                className={cn(
                  'rounded-pill border px-[9px] py-[3px] text-[10.5px] font-semibold',
                  tag.tone === 'sens'
                    ? 'border-transparent bg-sens-bg text-sens'
                    : 'border-line bg-field text-text-2',
                )}
              >
                {tag.label}
              </span>
            ))}
            {onAddTag && (
              <button
                type="button"
                onClick={onAddTag}
                className="rounded-pill border border-dashed border-line px-[9px] py-[3px] text-[10.5px] font-semibold text-text-3 transition-colors hover:text-text-2"
              >
                + Añadir
              </button>
            )}
          </div>
        </div>
      )}

      {notes && (
        <div className="px-4 py-[13px]">
          <div className="mb-[9px] text-[10px] font-bold uppercase tracking-[0.06em] text-text-2">
            Notas
          </div>
          <p className="text-[11px] leading-normal text-text-2">{notes}</p>
        </div>
      )}
    </aside>
  )
}
